/**
 * Custom hook for drag-and-drop scheduling logic
 *
 * Manages drag state, calculations, and API updates for rescheduling classes
 * via drag-and-drop interactions in the schedule grid.
 */

import { useState, useCallback } from 'react';
import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { useUpdateScheduledClassMutation } from '../../../api/schedulingApi';
import { timeToMinutes } from '../tabs/ScheduleGridTab/utils/classPositioning';
import type { ScheduledClass } from '../../../types/scheduling';

interface DragState {
  activeClassId: string | null;
  isDragging: boolean;
}

interface DropCalculation {
  day: string;
  start_time: string;
  end_time: string;
}

interface UndoAction {
  classId: string;
  previousData: {
    day: string;
    start_time: string;
    end_time: string;
  };
}

export const useDragAndDrop = (
  scheduledClasses: ScheduledClass[],
  timeIncrement: number,
  studioId: string,
  onSuccess?: (message: string) => void,
  onError?: (message: string) => void
) => {
  const [updateScheduledClass] = useUpdateScheduledClassMutation();
  const [dragState, setDragState] = useState<DragState>({
    activeClassId: null,
    isDragging: false,
  });
  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);

  /**
   * Calculate new day/time based on drop position
   * Snaps to 15-minute increments for precision
   */
  const calculateDropPosition = useCallback(
    (
      dropDay: string,
      dropTime: string,
      classDurationMinutes: number
    ): DropCalculation => {
      const startTimeMinutes = timeToMinutes(dropTime);

      // Snap to 15-minute increments
      const snappedMinutes = Math.round(startTimeMinutes / 15) * 15;
      const endTimeMinutes = snappedMinutes + classDurationMinutes;

      const formatTime = (minutes: number): string => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
      };

      return {
        day: dropDay,
        start_time: formatTime(snappedMinutes),
        end_time: formatTime(endTimeMinutes),
      };
    },
    []
  );

  /**
   * Handle drag start
   */
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDragState({
      activeClassId: event.active.id as string,
      isDragging: true,
    });
  }, []);

  /**
   * Handle drag end - update class position
   */
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      setDragState({
        activeClassId: null,
        isDragging: false,
      });

      if (!over) {
        return; // Dropped outside valid drop zone
      }

      const classId = active.id as string;
      const scheduledClass = scheduledClasses.find(sc => sc.id === classId);

      if (!scheduledClass) {
        return;
      }

      // Parse drop zone ID (format: "cell-Monday-09:00")
      const dropZoneId = over.id as string;
      const [, dropDay, dropTime] = dropZoneId.split('-');

      if (!dropDay || !dropTime) {
        return;
      }

      // Calculate class duration
      const startMinutes = timeToMinutes(scheduledClass.start_time);
      const endMinutes = timeToMinutes(scheduledClass.end_time);
      const durationMinutes = endMinutes - startMinutes;

      // Calculate new position
      const newPosition = calculateDropPosition(
        dropDay,
        dropTime,
        durationMinutes
      );

      // Check if position actually changed
      if (
        scheduledClass.day === newPosition.day &&
        scheduledClass.start_time === newPosition.start_time
      ) {
        return; // No change
      }

      // Save to undo stack
      const undoAction: UndoAction = {
        classId: scheduledClass.id,
        previousData: {
          day: scheduledClass.day,
          start_time: scheduledClass.start_time,
          end_time: scheduledClass.end_time,
        },
      };
      setUndoStack(prev => [...prev, undoAction]);

      try {
        // Update via API with manual flag set
        await updateScheduledClass({
          id: scheduledClass.id,
          studioId,
          scheduleId: scheduledClass.schedule,
          data: {
            ...newPosition,
            is_manually_scheduled: true, // Auto-mark as manually scheduled
          },
        }).unwrap();

        if (onSuccess) {
          onSuccess(
            `Moved ${scheduledClass.class_details.class_name} to ${newPosition.day} at ${newPosition.start_time.substring(0, 5)}`
          );
        }
      } catch (error) {
        console.error('Failed to update scheduled class:', error);
        if (onError) {
          onError('Failed to move class. Please try again.');
        }
        // Remove from undo stack if update failed
        setUndoStack(prev => prev.slice(0, -1));
      }
    },
    [
      scheduledClasses,
      calculateDropPosition,
      updateScheduledClass,
      studioId,
      onSuccess,
      onError,
    ]
  );

  /**
   * Undo last drag-and-drop action
   */
  const undoLastMove = useCallback(async () => {
    if (undoStack.length === 0) {
      return;
    }

    const lastAction = undoStack[undoStack.length - 1];
    const scheduledClass = scheduledClasses.find(
      sc => sc.id === lastAction.classId
    );

    if (!scheduledClass) {
      return;
    }

    try {
      await updateScheduledClass({
        id: lastAction.classId,
        studioId,
        scheduleId: scheduledClass.schedule,
        data: lastAction.previousData,
      }).unwrap();

      // Remove from undo stack
      setUndoStack(prev => prev.slice(0, -1));

      if (onSuccess) {
        onSuccess('Move undone successfully');
      }
    } catch (error) {
      console.error('Failed to undo move:', error);
      if (onError) {
        onError('Failed to undo move. Please try again.');
      }
    }
  }, [
    undoStack,
    scheduledClasses,
    updateScheduledClass,
    studioId,
    onSuccess,
    onError,
  ]);

  return {
    dragState,
    handleDragStart,
    handleDragEnd,
    undoLastMove,
    canUndo: undoStack.length > 0,
  };
};
